/**
 * career-workshop-list — the Workshops area (route `/workshops`) as a ROUTER, not a
 * report (operator ruling 2026-07-30). The hub's whole job is to get the person INTO a
 * workshop, so it is a state-aware hero plus FOUR consistently-presented smart links:
 * hero image · workshop name · ONE headline · a forward mark, with the entire row as
 * the link. Honest gate state stays (AX-10 gated ≠ broken): a locked entry is
 * non-interactive, carries the lock, and names its blocking predecessor in a readable
 * line rather than only a title attribute.
 *
 * What this surface DELIBERATELY no longer does — the previous S3 »process overview«
 * did all of it, and the operator's verdict was that it presents results instead of
 * routing:
 *   - no »Das machst du« / »Das bringt es dir« paragraph pair per workshop;
 *   - no inline W1 stage rail, no disclosed value pyramid / Schwartz wheel / statements
 *     (those are the workshops' OWN result faces — `/workshops/values` and the Kompass
 *     views present them, and the left pane still carries every W1 stage entry point,
 *     which is what `__tests__/nav-integrity.test.ts` asserts);
 *   - no step-count chips.
 * Consequently the three per-stage outcome fetches are gone too: the hub reads exactly
 * one endpoint.
 *
 * Shape: `.ws-link` rides the §12.1 CUT-CORNER primitive, the same shape as
 * `<career-next-step>` — a hub of forward affordances should look like one.
 *
 * Fetch-driven off `workshops.list`; the workshop push streams reconcile live gate
 * flips without a reload.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, PushToken, RouterToken, SessionToken, WorkshopStoreToken, apiGet } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { careerStream, workshopUrn, type ShellSession, type WorkshopStore } from "../domain"
import { acquireStreams, onChange } from "../lib/push-streams"
import { hubHero, HUB_TITLE_ID } from "../lib/hub"
import { GLYPH } from "../lib/icons"

type Gate = "locked" | "available" | "in_progress" | "completed"

type W1StageKey = "cardgame" | "interview" | "sektion"
type W1StageStatus = "done" | "current" | "todo"

interface W1Stage {
  key: W1StageKey
  status: W1StageStatus
  href: string
  round: number | null
  roundTotal: number | null
}

interface WorkshopSummaryView {
  type: string
  slug: string
  name: string
  purpose: string
  gate: Gate
  blockedBy: { slug: string; name: string } | null
  stepCount: number
  answeredCount: number
  hasContext: boolean
  /** W1 ONLY: the three-stage »Deine Werte« track (absent on W2/W3/W4). Read here only to
   *  aim the hero's ONE resume CTA at the exact stage the person stopped on. */
  stages?: W1Stage[]
}

/** PRESENTATION order — also the push-subscription set. NOT a chain: each workshop locks from
 *  its OWN server-side prerequisite (W4 »Schreibstil« hangs off W1, not off W3), so this list
 *  fixes the reading order only and every entry states its own honest gate. A workshop absent
 *  from the api view renders no link — the hub never invents a workshop. */
const WORKSHOP_TYPES = ["self_knowledge", "search_profile", "profiler", "writing_style"] as const

/** The hero image slug per workshop — `public/heroes/<slug>.{light,dark}.webp`, served by the
 *  BFF's `/heroes/:file` route. `werte` is the existing Kompass hero, reused here so W1 keeps
 *  ONE picture across the app. A workshop with no slug falls back to the brand gradient. */
const HERO_SLUG: Record<string, string | undefined> = {
  self_knowledge: "werte",
  search_profile: "suchprofil",
  profiler: "entscheidung",
  writing_style: "schreibstil",
}

/** The ONE headline per workshop — what the person GETS, in a single line. No second
 *  sentence: the workshop itself explains the workshop. */
const HEADLINE: Record<string, CareerKey | undefined> = {
  self_knowledge: "workshops.link.selfKnowledge",
  search_profile: "workshops.link.searchProfile",
  profiler: "workshops.link.profiler",
  writing_style: "workshops.link.writingStyle",
}

const GO = html`<svg viewBox="0 0 24 24" aria-hidden="true">${GLYPH.chevronRight}</svg>`
const LOCK = html`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 10V8a6 6 0 0 1 12 0v2" /><rect x="4" y="10" width="16" height="10" rx="2" /></svg>`

export class WorkshopListVm implements ViewModel {
  readonly workshops: Signal<WorkshopSummaryView[]> = signal<WorkshopSummaryView[]>([])
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(
    private readonly router: Router,
    private readonly push: PushChannel,
    private readonly store: WorkshopStore,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.renderDeps = [this.workshops, this.error, this.loaded]
  }

  attach(): () => void {
    void this.refresh()
    const userId = this.session.user?.sub
    let stop = () => {}
    if (userId) {
      const release = acquireStreams(
        this.push,
        WORKSHOP_TYPES.map((t) => careerStream(userId, "Workshop", workshopUrn(userId, t))),
      )
      // Live gate flips (completion in another tab) → re-fetch the summary.
      const off = onChange(this.store.list("alle"), () => void this.refresh())
      stop = () => {
        off()
        release()
      }
    }
    return stop
  }

  private async refresh(): Promise<void> {
    try {
      this.workshops.set(await apiGet<WorkshopSummaryView[]>("/api/workshops"))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Navigate by its English route (AX-9). */
  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerWorkshopList extends KitElement<WorkshopListVm> {
  protected createViewModel(ctx: KitContext): WorkshopListVm {
    return new WorkshopListVm(
      ctx.inject(RouterToken),
      ctx.inject(PushToken),
      ctx.inject(WorkshopStoreToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  /** The state-derived hero: the ONE recommended action. Nothing started → into the
   *  Kartenspiel; mid-track → resume the exact stage; the whole W1 track done → onward to
   *  the Kompass. The four smart links below are the full map; this is the default move. */
  private hero(vm: WorkshopListVm): TemplateResult {
    const stages = vm.workshops.value.find((w) => w.type === "self_knowledge")?.stages ?? []
    const done = stages.filter((s) => s.status === "done").length
    const current = stages.find((s) => s.status === "current")
    const eyebrow = vm.t("workshops.hub.eyebrow")
    if (stages.length > 0 && done >= stages.length) {
      return hubHero({
        eyebrow,
        title: vm.t("workshops.hub.title.done"),
        lead: vm.t("workshops.hub.lead.done"),
        cta: { label: vm.t("workshops.hub.cta.compass"), act: "nav", url: "/compass" },
      })
    }
    if (done > 0 && current) {
      return hubHero({
        eyebrow,
        title: vm.t("workshops.hub.title.mid"),
        lead: vm.t("workshops.hub.lead.mid"),
        cta: { label: vm.t("workshops.hub.cta.resume"), act: "nav", url: current.href },
      })
    }
    return hubHero({
      eyebrow,
      title: vm.t("workshops.hub.title.empty"),
      lead: vm.t("workshops.hub.lead.empty"),
      cta: { label: vm.t("workshops.hub.cta.start"), act: "nav", url: current?.href ?? "/workshops/values" },
    })
  }

  /** ONE smart link. Identical anatomy for all four workshops — hero image · name · one
   *  headline · forward mark — so the hub reads as a menu of equals rather than a ranked
   *  process. The whole row is the affordance; there is no separate »Öffnen« button.
   *
   *  Locked: a non-interactive block (square + dashed, §12.1 — no cut corner, because
   *  there is no way through yet) carrying the lock and the readable blocking reason. */
  private smartLink(vm: WorkshopListVm, w: WorkshopSummaryView): TemplateResult {
    const locked = w.gate === "locked"
    const url = `/workshops/${w.slug}`
    const slug = HERO_SLUG[w.type]
    // Both custom properties are always set together: `.hero-tile-media` composes them WITH
    // the brand gradient, and an unset one would invalidate the whole background-image.
    const media = slug ? `--hero-d:url(/heroes/${slug}.dark.webp);--hero-l:url(/heroes/${slug}.light.webp)` : nothing
    const head = HEADLINE[w.type]
    const reason = locked && w.blockedBy ? vm.t("workshop.lockedBy", { name: w.blockedBy.name }) : null
    const inner = html`
      <span class="ws-link-media hero-tile-media" style=${media} aria-hidden="true"></span>
      <span class="ws-link-body">
        <span class="ws-link-name" lang="de">${w.name}</span>
        <span class="ws-link-head" lang="de">${head ? vm.t(head) : w.purpose}</span>
        ${reason ? html`<span class="ws-link-reason" lang="de">${reason}</span>` : nothing}
      </span>
      <span class="ws-link-go">${locked ? LOCK : GO}</span>
    `
    return html`
      <li class="ws-link-item" urn=${`urn:career:workshop:${w.slug}` as Urn}>
        ${locked
          ? html`<div class=${classMap({ "ws-link": true, "is-locked": true })} aria-disabled="true" title=${reason ?? nothing}>${inner}</div>`
          : html`<a class="ws-link" href=${url} data-act="nav" data-url=${url} lang="de">${inner}</a>`}
      </li>
    `
  }

  protected render(vm: WorkshopListVm): TemplateResult {
    const err = vm.error.value
    // Pipeline order is the reading order, not the api's list order.
    const ordered = WORKSHOP_TYPES.map((t) => vm.workshops.value.find((w) => w.type === t)).filter(
      (w): w is WorkshopSummaryView => w !== undefined,
    )
    return html`
      <section class="surface workshops workshops-hub" aria-labelledby=${HUB_TITLE_ID}>
        ${this.hero(vm)}
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${ordered.length > 0
          ? html`<ul class="ws-links" role="list">${ordered.map((w) => this.smartLink(vm, w))}</ul>`
          : nothing}
      </section>
    `
  }

  protected override actions(vm: WorkshopListVm): ActionMap {
    return {
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
